import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, BadgeCheck, ShieldCheck, Handshake, Calendar, Clock, Timer, Network, HeartPulse, Star } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Section";
import { ProductCard } from "@/components/ProductCard";
import { OfferPopup } from "@/components/OfferPopup";
import { getProducts } from "@/lib/api";
import { formatNewsDate } from "@/data/news";
import { GROUP_STRENGTH } from "@/data/site";
import { useContent } from "@/context/ContentContext";

const ICONS = {
  truck: Truck,
  "badge-check": BadgeCheck,
  "shield-check": ShieldCheck,
  handshake: Handshake,
  clock: Clock,
  timer: Timer,
  network: Network,
  "heart-pulse": HeartPulse,
};

const productBlurb = (p) => {
  if (p.description) return p.description;
  const text =
    p.category === "Engine Oil"
      ? `${p.specs?.Grade || ""} engine oil${p.specs?.Volume ? " • " + p.specs.Volume : ""}`
      : `${p.highlight || ""}${p.specs?.Size ? " • " + p.specs.Size : ""}`;
  const clean = text.replace(/^\s*•\s*/, "").trim();
  return clean || null;
};

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    getProducts().then((all) => {
      const flagged = all.filter((p) => p.is_featured);
      if (flagged.length) {
        setFeatured(flagged.slice(0, 4));
      } else {
        const tyres = all.filter((p) => p.category === "Tyre").slice(0, 4);
        const oils = all.filter((p) => p.category === "Engine Oil").slice(0, 4);
        setFeatured([...tyres.slice(0, 3), ...oils.slice(0, 1)]);
      }
    });
  }, []);

  const { content, news } = useContent();
  const COMPANY = content.company || {};
  const STATS = content.stats || [];
  const BRANDS = content.brands || [];
  const SERVICES = content.services || [];
  const REVIEWS = content.reviews || [];
  const HERO_IMAGE = COMPANY.heroImage;
  const MAP_IMAGE = COMPANY.mapImage;

  return (
    <div data-testid="home-page">
      <OfferPopup />
      {/* HERO */}
      <section className="relative overflow-hidden carbon-texture">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="RTPL two-wheeler and three-wheeler service workshop" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6 md:py-40 lg:px-8">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block border-l-4 border-primary pl-3 text-xs font-bold uppercase tracking-[0.3em] text-secondary"
          >
            Est. {COMPANY.established} · Dhaka, Bangladesh
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 max-w-4xl font-heading text-5xl font-bold leading-[0.95] tracking-tight text-white md:text-7xl"
          >
            {COMPANY.heroTitle || "Driving Bangladesh Forward With"}
            <span className="mt-2 block text-primary">
              {COMPANY.heroTitleAccent || "RUNNER Trade Park LTD"}
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75"
          >
            {COMPANY.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-products-btn"
              className="group flex items-center gap-2 bg-primary px-7 py-3 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:bg-primary/90 shadow-md hover:shadow-lg"
            >
              Explore Products
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/support"
              data-testid="hero-support-btn"
              className="border-2 border-white/60 px-7 py-3 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:border-primary hover:text-primary"
            >
              Become a Dealer
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-border md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="px-6 py-10 text-center">
              <div className="font-heading text-4xl font-bold text-primary md:text-5xl">
                {s.value}
              </div>
              <div className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PARTNER BRAND MARQUEE */}
      <section className="overflow-hidden border-b border-border bg-background py-12">
        <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground">
          Proudly Distributing
        </p>
        <div className="flex w-max animate-marquee items-center gap-8 px-4">
          {[...BRANDS, ...BRANDS, ...BRANDS, ...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
            <div
              key={i}
              data-testid={`marquee-logo-${i}`}
              className="flex h-16 items-center rounded-lg bg-white px-8 shadow-sm"
            >
              <img src={b.logo} alt={b.name} className="h-9 w-auto object-contain" />
            </div>
          ))}
        </div>
      </section>

      {/* GROUP STRENGTH */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow="Our Group Strength"
          title="Built On Precision & Trust"
          description="RTPL embodies precision, reliability and authenticity in every facet of its operations across the nation."
        />
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {(content.group_strength && content.group_strength.length ? content.group_strength : GROUP_STRENGTH).map((g, i) => {
            const Icon = ICONS[g.icon] || BadgeCheck;
            return (
              <Reveal key={g.title || i} delay={i * 0.08}>
                <div className="group h-full bg-card p-8 transition-colors hover:bg-primary">
                  <Icon className="h-9 w-9 text-primary transition-colors group-hover:text-white" />
                  <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight text-foreground transition-colors group-hover:text-white">
                    {g.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-white/85">
                    {g.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FACILITIES & SERVICES */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeading
            eyebrow="What We Offer"
            title="Facilities & Services"
            description="More than products — a complete support ecosystem that keeps you moving with confidence."
          />
          <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full bg-card p-8 transition-colors hover:bg-primary">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-white/20 group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-white">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-white/85">
                      {s.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading
              eyebrow="What We Work With"
              title="Featured Products"
              description="Genuine tyres and lubricants trusted by riders and workshops nationwide."
            />
            <Link
              to="/products"
              data-testid="featured-viewall-btn"
              className="mb-12 flex items-center gap-2 font-heading text-sm font-bold tracking-wide text-primary hover:gap-3 transition-all"
            >
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard product={p} description={productBlurb(p)} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS TEASER */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow="Partner Brands"
          title="The Brands We Champion"
          align="center"
        />
        <div className="grid gap-6 md:grid-cols-3">
          {BRANDS.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.08}>
              <div className="flex h-full flex-col border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-xl group">
                <div className="mb-6 flex h-16 w-fit items-center rounded-lg bg-white px-6 shadow-sm transition-all duration-300 group-hover:shadow-md">
                  <img src={b.logo} alt={b.name} className="h-9 w-auto object-contain transition-transform duration-300 group-hover:scale-110" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                  {b.tag}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/brands"
            data-testid="brands-viewall-btn"
            className="inline-flex items-center gap-2 border-2 border-primary px-7 py-3 font-heading text-sm font-bold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white"
          >
            Explore Partner Brands <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeading
            eyebrow="Customer Voices"
            title="What Our Customers Say"
            description="Real experiences from riders, workshops and dealers across Bangladesh."
            align="center"
          />
          <div className="grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.08}>
                <div
                  data-testid={`review-card-${i}`}
                  className="flex h-full flex-col border border-border bg-background p-8"
                >
                  <div className="flex gap-1 text-amber-400">
                    {Array.from({ length: r.rating }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">
                    &ldquo;{r.text}&rdquo;
                  </p>
                  <div className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                    {r.image ? (
                      <img
                        src={r.image}
                        alt={r.name}
                        className="h-11 w-11 shrink-0 rounded-full border border-border object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-white">
                        {r.name.charAt(0)}
                      </span>
                    )}
                    <div>
                      <div className="text-sm font-bold text-foreground">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.location}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS & EVENTS */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading
              eyebrow="Latest Updates"
              title="News & Events"
              description="Announcements, partnerships and milestones from the RTPL family."
            />
            <Link
              to="/news"
              data-testid="news-viewall-btn"
              className="mb-12 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-primary transition-all hover:gap-3"
            >
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {news.slice(0, 3).map((n, i) => (
              <Reveal key={n.id} delay={i * 0.08}>
                <Link
                  to={`/news?post=${n.id}`}
                  data-testid={`home-news-${n.id}`}
                  className="group flex h-full flex-col overflow-hidden border border-border bg-background transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-xl"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={n.image}
                      alt={n.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-0 top-0 bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      {n.tag}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5 text-primary" />
                      {formatNewsDate(n.date)}
                    </span>
                    <h3 className="mt-2 font-heading text-lg font-semibold leading-tight tracking-tight text-foreground">
                      {n.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                      {n.excerpt}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DEALER NETWORK CTA */}
      <section className="relative overflow-hidden">
        <img src={MAP_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
              Nationwide Coverage
            </span>
            <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white md:text-5xl">
              A Dealer Network You Can Rely On
            </h2>
            <p className="mt-4 text-lg text-white/75">
              From Dhaka to every division, our distribution reaches riders and workshops across all
              64 districts of Bangladesh.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/dealer-network"
                data-testid="home-dealer-btn"
                className="bg-primary px-7 py-3 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:bg-primary/90 shadow-md hover:shadow-lg"
              >
                View Network
              </Link>
              <Link
                to="/support"
                className="border-2 border-white/60 px-7 py-3 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:border-secondary hover:text-secondary"
              >
                Become a Dealer
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
